import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";

import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import WABAFormPreview from "./WabaFormPreview";

import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";

const getStatusColor = (status) => {
  switch ((status || "").toUpperCase()) {
    case "PUBLISHED":
      return "success";
    case "DRAFT":
      return "warning";
    case "BLOCKED":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const getFlowJsonStats = (json) => {
  const screens = Array.isArray(json?.screens) ? json.screens : [];
  const components = screens.reduce(
    (total, screen) =>
      total +
      (Array.isArray(screen?.layout?.children)
        ? screen.layout.children.length
        : 0),
    0
  );

  const size = new Blob([JSON.stringify(json || {})]).size;

  return {
    screens: screens.length,
    components,
    sizeKb: size / 1024,
    version: json?.version || "-",
    dataApiVersion: json?.data_api_version || null,
  };
};

const DetailRow = ({ label, value, monospace = false }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    spacing={2}
    sx={{ py: 0.8 }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>

    <Typography
      variant="body2"
      fontWeight={600}
      textAlign="right"
      sx={{
        minWidth: 0,
        wordBreak: "break-word",
        fontFamily: monospace
          ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
          : "inherit",
      }}
    >
      {value ?? "-"}
    </Typography>
  </Stack>
);

const ImplementationGuide = ({ hasEndpoint }) => {
  const endpointPayloadExample = `{
  "encrypted_aes_key": "<base64 RSA-encrypted AES key>",
  "encrypted_flow_data": "<base64 encrypted flow payload>",
  "initial_vector": "<base64 IV>"
}`;

  const decryptedPingExample = `{
  "version": "3.0",
  "action": "ping"
}`;

  const pingResponseExample = `{
  "data": {
    "status": "active"
  }
}`;

  const requestHandlerExample = `app.post("/whatsapp-flow", async (req, res) => {
  try {
    const {
      encrypted_aes_key,
      encrypted_flow_data,
      initial_vector,
    } = req.body;

    // 1. Decrypt encrypted_aes_key using your RSA private key.
    // 2. Decrypt encrypted_flow_data using the decrypted AES key + IV.
    const { body, aesKey, iv } = decryptRequest({
      encrypted_aes_key,
      encrypted_flow_data,
      initial_vector,
    });

    let response;

    if (body.action === "ping") {
      response = {
        data: {
          status: "active",
        },
      };
    } else if (body.action === "INIT") {
      response = {
        screen: "FIRST_SCREEN",
        data: {},
      };
    } else if (body.action === "data_exchange") {
      response = {
        screen: "NEXT_SCREEN",
        data: {},
      };
    } else {
      response = {
        data: {
          error: "Unsupported action",
        },
      };
    }

    // Encrypt using this request's AES key.
    // WhatsApp Flow endpoint responses use the flipped IV.
    const encryptedResponse = encryptResponse({
      response,
      aesKey,
      iv,
    });

    return res.status(200).send(encryptedResponse);
  } catch (error) {
    console.error("Flow endpoint error:", error);
    return res.sendStatus(500);
  }
});`;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "action.hover",
              color: "text.secondary",
            }}
          >
            <DnsOutlinedIcon fontSize="small" />
          </Box>

          <Box>
            <Typography fontWeight={800}>Endpoint implementation</Typography>
            <Typography variant="body2" color="text.secondary">
              Needed when the Flow uses data exchange with your backend.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {!hasEndpoint && (
        <Alert
          severity="warning"
          icon={<WarningAmberOutlinedIcon />}
          sx={{ mx: 2, mb: 2 }}
        >
          No endpoint URI is configured. Static navigation can still work, but
          data_exchange requires a Flow endpoint.
        </Alert>
      )}

      <Divider />

      <Accordion disableGutters elevation={0} square>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockOutlinedIcon fontSize="small" color="action" />
            <Typography fontWeight={700}>
              Request / response encryption
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" paragraph>
            WhatsApp posts an encrypted request to your Flow endpoint. Your
            backend decrypts the AES key with its RSA private key, decrypts the
            Flow payload with that AES key, processes the action, then returns
            an encrypted response.
          </Typography>

          <Typography variant="caption" fontWeight={800}>
            Incoming HTTP body
          </Typography>

          <SyntaxHighlighter
            language="json"
            style={atomDark}
            customStyle={{
              borderRadius: 8,
              fontSize: "0.78rem",
              marginTop: 8,
            }}
          >
            {endpointPayloadExample}
          </SyntaxHighlighter>

          <Alert severity="info" sx={{ mt: 1.5 }}>
            Keep the RSA private key on your backend. Register only the matching
            public key with WhatsApp.
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} square>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon fontSize="small" color="action" />
            <Typography fontWeight={700}>Meta endpoint ping</Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" paragraph>
            Treat the endpoint health check as a Flow endpoint action. After
            decrypting the request, handle the ping action and encrypt the
            response before returning it.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" fontWeight={800}>
                Decrypted request
              </Typography>
              <SyntaxHighlighter
                language="json"
                style={atomDark}
                customStyle={{
                  borderRadius: 8,
                  fontSize: "0.78rem",
                  marginTop: 8,
                }}
              >
                {decryptedPingExample}
              </SyntaxHighlighter>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" fontWeight={800}>
                Response before encryption
              </Typography>
              <SyntaxHighlighter
                language="json"
                style={atomDark}
                customStyle={{
                  borderRadius: 8,
                  fontSize: "0.78rem",
                  marginTop: 8,
                }}
              >
                {pingResponseExample}
              </SyntaxHighlighter>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} square>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CodeOutlinedIcon fontSize="small" color="action" />
            <Typography fontWeight={700}>Express endpoint skeleton</Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" paragraph>
            Keep cryptography in dedicated helpers and private keys out of
            source code.
          </Typography>

          <SyntaxHighlighter
            language="javascript"
            style={atomDark}
            customStyle={{
              borderRadius: 8,
              fontSize: "0.78rem",
              margin: 0,
            }}
          >
            {requestHandlerExample}
          </SyntaxHighlighter>

          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Example endpoint code is only a starting point. Add validation,
            secret management, logging and production error handling.
          </Alert>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

const FlowDetails = () => {
  const { id } = useParams();

  const [publishing, setPublishing] = useState(false);
  const [updatingEndpoint, setUpdatingEndpoint] = useState(false);
  const [endpoint, setEndpoint] = useState("");
  const [endpointTouched, setEndpointTouched] = useState(false);

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/api/get-flow-asset/${id}`
  );

  useEffect(() => {
    if (data?.detail) {
      setEndpoint(data.detail.endpoint_uri ?? "");
      setEndpointTouched(false);
    }
  }, [data]);

  const stats = useMemo(() => getFlowJsonStats(data?.json), [data?.json]);

  const endpointValidation = useMemo(() => {
    const trimmed = endpoint.trim();

    if (!trimmed) {
      return {
        valid: false,
        message: "Endpoint is required.",
      };
    }

    try {
      const parsed = new URL(trimmed);

      if (parsed.protocol !== "https:") {
        return {
          valid: false,
          message: "Use an HTTPS endpoint.",
        };
      }

      return {
        valid: true,
        message: "",
      };
    } catch {
      return {
        valid: false,
        message: "Enter a valid HTTPS URL.",
      };
    }
  }, [endpoint]);

  const handleUpdateEndpoint = useCallback(async () => {
    setEndpointTouched(true);

    if (!endpointValidation.valid) {
      toast.error(endpointValidation.message);
      return;
    }

    try {
      setUpdatingEndpoint(true);

      const response = await axios.post(
        `${API_ENDPOINT}/api/update-flow-metadata/${id}`,
        {
          endpoint_uri: endpoint.trim(),
        },
        getHeader()
      );

      await refetch();

      toast.success(
        response?.data?.message || "Endpoint updated successfully!"
      );
    } catch (error) {
      toastError(error);
    } finally {
      setUpdatingEndpoint(false);
    }
  }, [id, endpoint, endpointValidation, refetch]);

  const publishFlow = useCallback(async () => {
    try {
      setPublishing(true);

      const response = await axios.post(
        `${API_ENDPOINT}/api/publish-flow/${id}`,
        {},
        getHeader()
      );

      await refetch();

      toast.success(response?.data?.message || "Flow published successfully!");
    } catch (error) {
      toastError(error);
    } finally {
      setPublishing(false);
    }
  }, [id, refetch]);

  const copyToClipboard = useCallback(async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  }, []);

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      </Container>
    );
  }

  const detail = data?.detail || {};
  const status = detail?.status || "UNKNOWN";
  const isPublished = status === "PUBLISHED";
  const hasEndpoint = Boolean(detail?.endpoint_uri?.trim());

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 2,
        px: { xs: 1.5, sm: 2, xl: 3 },
        minHeight: "100vh",
        bgcolor: "#f6f7f9",
      }}
    >
      <Box sx={{ mb: 1 }}>
        <BackBtn />
      </Box>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          borderRadius: 3,
          px: { xs: 1.5, md: 2 },
          py: 1.5,
          mb: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box minWidth={0}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#e8f5e9",
                  color: "#137333",
                  flexShrink: 0,
                }}
              >
                <SmartphoneOutlinedIcon />
              </Box>

              <Box minWidth={0}>
                <PageTitle>{detail?.name || "Flow Details"}</PageTitle>

                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  mt={0.5}
                >
                  <Chip
                    size="small"
                    color={getStatusColor(status)}
                    label={status}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${stats.screens} screen${
                      stats.screens === 1 ? "" : "s"
                    }`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${stats.components} components`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`JSON ${stats.version}`}
                  />
                  {stats.dataApiVersion && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Data API ${stats.dataApiVersion}`}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: "flex-start", lg: "flex-end" }}
          >
            <Tooltip title="Reload flow details">
              <IconButton onClick={() => refetch()}>
                <RefreshOutlinedIcon />
              </IconButton>
            </Tooltip>

            <Button
              component={Link}
              to={`/home/${id}/forms-flow-builder`}
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
            >
              Open Builder
            </Button>

            <Button
              component={Link}
              to={`/home/${id}/flow-json-editor`}
              variant="outlined"
              startIcon={<CodeOutlinedIcon />}
            >
              JSON Editor
            </Button>

            {!isPublished && (
              <LoadingButton
                loading={publishing}
                onClick={publishFlow}
                variant="contained"
                startIcon={<PublishOutlinedIcon />}
              >
                Publish
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 3, overflow: "hidden" }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.4,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Box>
                  <Typography fontWeight={800}>Flow preview</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current saved Flow JSON rendered as a mobile experience.
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  variant="outlined"
                  label={`${stats.sizeKb.toFixed(1)} KB`}
                />
              </Stack>
            </Box>

            <Box
              sx={{
                minHeight: 680,
                py: 3,
                px: { xs: 1, sm: 2 },
                bgcolor: "#eef1f4",
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(0,0,0,.07) 1px, transparent 0)",
                backgroundSize: "18px 18px",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                overflow: "auto",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 500 }}>
                <WABAFormPreview formStructure={data?.json} />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ borderRadius: 3 }}>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoOutlinedIcon color="action" />
                  <Typography fontWeight={800}>Flow overview</Typography>
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ px: 2, py: 1 }}>
                <DetailRow label="Flow ID" value={detail?.id || id} monospace />
                <Divider />
                <DetailRow label="Name" value={detail?.name || "-"} />
                <Divider />
                <DetailRow label="Status" value={status} />
                <Divider />
                <DetailRow
                  label="Categories"
                  value={
                    Array.isArray(detail?.categories)
                      ? detail.categories.join(", ")
                      : detail?.categories || "-"
                  }
                />
                <Divider />
                <DetailRow
                  label="Created"
                  value={formatDate(
                    detail?.created_time ||
                      detail?.createdOn ||
                      detail?.created_at
                  )}
                />
                <Divider />
                <DetailRow
                  label="Updated"
                  value={formatDate(
                    detail?.updated_time ||
                      detail?.updatedOn ||
                      detail?.updated_at
                  )}
                />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 3 }}>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DataObjectOutlinedIcon color="action" />
                  <Typography fontWeight={800}>Flow JSON</Typography>
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ px: 2, py: 1 }}>
                <DetailRow label="Version" value={stats.version} />
                <Divider />
                <DetailRow
                  label="Data API"
                  value={stats.dataApiVersion || "Not used"}
                />
                <Divider />
                <DetailRow label="Screens" value={stats.screens} />
                <Divider />
                <DetailRow label="Components" value={stats.components} />
                <Divider />
                <DetailRow
                  label="Payload size"
                  value={`${stats.sizeKb.toFixed(1)} KB`}
                />
              </Box>

              <Box sx={{ px: 2, pb: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyOutlinedIcon />}
                  onClick={() =>
                    copyToClipboard(
                      JSON.stringify(data?.json || {}, null, 2),
                      "Flow JSON copied."
                    )
                  }
                >
                  Copy Flow JSON
                </Button>
              </Box>
            </Paper>

            {isPublished ? (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon />}
                sx={{ borderRadius: 3 }}
              >
                This Flow is published.
              </Alert>
            ) : (
              <Alert
                severity="info"
                icon={<PublishOutlinedIcon />}
                sx={{ borderRadius: 3 }}
              >
                Review the preview and endpoint configuration before publishing.
              </Alert>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          mt: 2,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "action.hover",
                  color: "text.secondary",
                }}
              >
                <LinkOutlinedIcon fontSize="small" />
              </Box>

              <Box>
                <Typography fontWeight={800}>Flow endpoint</Typography>
                <Typography variant="body2" color="text.secondary">
                  Backend URL used by data exchange actions.
                </Typography>
              </Box>
            </Stack>

            <Chip
              size="small"
              color={hasEndpoint ? "success" : "warning"}
              variant="outlined"
              label={hasEndpoint ? "Configured" : "Not configured"}
            />
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems="flex-start"
          >
            <TextField
              fullWidth
              value={endpoint}
              onChange={(e) => {
                setEndpoint(e.target.value);
                setEndpointTouched(true);
              }}
              onBlur={() => setEndpointTouched(true)}
              size="small"
              variant="outlined"
              label="HTTPS endpoint URL"
              placeholder="https://api.example.com/whatsapp-flow"
              error={endpointTouched && !endpointValidation.valid}
              helperText={
                endpointTouched && !endpointValidation.valid
                  ? endpointValidation.message
                  : "WhatsApp sends encrypted Flow endpoint requests to this URL."
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <LoadingButton
              loading={updatingEndpoint}
              onClick={handleUpdateEndpoint}
              variant="contained"
              disabled={!endpointValidation.valid}
              sx={{
                minWidth: { xs: "100%", md: 150 },
                height: 40,
              }}
            >
              Save endpoint
            </LoadingButton>
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <ImplementationGuide hasEndpoint={hasEndpoint} />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          mt: 2,
          overflow: "hidden",
        }}
      >
        <Accordion disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DataObjectOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography fontWeight={800}>Raw Flow metadata</Typography>
                <Typography variant="caption" color="text.secondary">
                  Exact metadata returned by your API.
                </Typography>
              </Box>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                size="small"
                startIcon={<ContentCopyOutlinedIcon />}
                onClick={() =>
                  copyToClipboard(
                    JSON.stringify(detail, null, 2),
                    "Flow metadata copied."
                  )
                }
              >
                Copy
              </Button>
            </Box>

            <SyntaxHighlighter
              language="json"
              style={atomDark}
              customStyle={{
                margin: 0,
                borderRadius: 0,
                fontSize: "0.82rem",
                maxHeight: 500,
              }}
            >
              {JSON.stringify(detail, null, 2)}
            </SyntaxHighlighter>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Container>
  );
};

export default FlowDetails;
